import { useTranslation } from "@opendash/core";
import { Descriptions, DescriptionsProps, Flex } from "antd";
import { DescriptionsItemType } from "antd/es/descriptions";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { GetTicketDataOptions, TicketData, useTicketData } from "../..";
import { AssignmentTag, PriorityTag, RecentMessage } from "../../..";
import { Ticket_Material } from "../../../../../js/parsegenerated";
import { ItemLabel } from "../../../../items";

type Props = DescriptionsProps & {
  options?: GetTicketDataOptions;
  column?: number;
};

type OrderedDescriptionsItemType = DescriptionsItemType & { order?: number };

export const TicketDynamicDescription = observer((props: Props) => {
  const t = useTranslation();
  const ticketData = useTicketData();

  const items = useMemo(() => {
    const itemData: OrderedDescriptionsItemType[] = Object.keys(ticketData).map(
      (attr: keyof TicketData) => {
        const itemObject: OrderedDescriptionsItemType = {
          children: "",
          span: 1,
          label: t("maintenance:general.ticket." + attr),
          order: 0,
        };

        if (attr === "ticket" && ticketData[attr]) {
          itemObject.children = ticketData[attr].get("title");
          itemObject.order = -1;
        }
        if (attr === "state" && ticketData[attr])
          itemObject.children = ticketData[attr].get("label");

        if (attr === "priority" && ticketData[attr])
          itemObject.children = <PriorityTag />;

        if (attr === "source" && ticketData[attr])
          itemObject.children = ticketData[attr].get("name");

        if (attr === "issuecategory" && ticketData[attr])
          itemObject.children = ticketData[attr].get("name");

        if (attr === "downtime" && ticketData[attr])
          itemObject.children = t(
            "maintenance:priority.select.downtime.slider." +
              ticketData[attr].get("value")
          );

        if (attr === "frequency" && ticketData[attr])
          itemObject.children = t(
            "maintenance:priority.select.frequency.slider." +
              ticketData[attr].get("value")
          );

        if (attr === "restriction" && ticketData[attr])
          itemObject.children = t(
            "maintenance:priority.select.restriction.slider." +
              ticketData[attr].get("value")
          );

        if (attr === "duedate" && ticketData[attr])
          itemObject.children = dayjs(ticketData[attr].get("end")).format(
            "DD.MM.YYYY HH:mm"
          );

        if (attr === "messages" && ticketData[attr]) {
          itemObject.children = <RecentMessage hideForm hideTime />;
          itemObject.span = props.column;
          itemObject.order = 10;
        }

        if (attr === "material" && ticketData[attr]) {
          const obj: Ticket_Material = ticketData[attr];
          itemObject.children = (
            <Flex
              vertical
              children={obj
                ?.get("itemList")
                .map((item) => (
                  <ItemLabel
                    key={item.id}
                    item={item.id.toString()}
                    quantity={item.quantity}
                  />
                ))}
            />
          );
        }

        if (attr === "assignedTo" && ticketData[attr]) {
          const obj: TicketData["assignedTo"] = ticketData[attr];
          itemObject.children = <AssignmentTag />;
        }

        if (itemObject.children === "") return null;

        return itemObject;
      }
    );

    return itemData.filter(Boolean);
  }, [ticketData]);

  items.sort((a, b) => a.order - b.order);

  // add span to ticket name so it fits the columns
  const spanCount = items.reduce<number>(
    (acc, item) => acc + Number.parseInt(item?.span?.toString()),
    0
  );
  const neededSpan = spanCount % props.column;
  if (items[0]) {
    items[0].span =
      Number.parseInt(items[0].span?.toString()) +
      (neededSpan > 0 ? neededSpan : 0);
  }

  return <Descriptions items={items} {...props} />;
});
